import React, { useEffect, useMemo, useState } from "react";
import { stratificationService } from "../../services/stratification.services";

const formatBoolean = (value) => (value ? "ANO" : "NE");

const renderList = (items = [], renderer) => {
  if (!items?.length) {
    return "Žádné informace";
  }

  return (
    <div className="stratification-info-list">
      {items.map((item, index) => (
        <div
          className="stratification-info-row"
          key={`${item.reference_code || item.reference_name || "item"}-${index}`}
        >
          {renderer(item)}
        </div>
      ))}
    </div>
  );
};

export const PatientStratificationInfo = ({ careId, patientName, onBack }) => {
  const [state, setState] = useState({
    loading: true,
    error: false,
    data: null,
  });

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const data = await stratificationService.getCareStratification(careId);
        if (!mounted) return;

        setState({
          loading: false,
          error: false,
          data,
        });
      } catch (error) {
        if (!mounted) return;

        setState({
          loading: false,
          error: true,
          data: null,
        });
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [careId]);

  const rows = useMemo(() => {
    if (!state.data) return [];

    return [
      {
        label: "Riziková léčiva",
        value: renderList(
          state.data.risk_drugs,
          (item) =>
            `${item.reference_name || "-"}${item.reference_code ? ` [${item.reference_code}]` : ""}${item.checkin_area ? ` | ${item.checkin_area}` : ""}`,
        ),
      },
      {
        label: "Riziková diagnóza",
        value: renderList(
          state.data.risk_diagnoses,
          (item) =>
            `${item.reference_name || "-"}${item.reference_code ? ` [${item.reference_code}]` : ""}`,
        ),
      },
      {
        label: "Rizikové laboratorní výsledky",
        value: renderList(
          state.data.lab_results,
          (item) =>
            `${item.reference_name || "-"}${item.reference_code ? ` [${item.reference_code}]` : ""}`,
        ),
      },
      {
        label: "Polypragmazie",
        value: state.data.polypharmacy ? (
          <div className="stratification-info-list">
            <div className="stratification-info-row">
              Počet léčiv: {state.data.polypharmacy.total_drug_count ?? "-"}
            </div>
            <div className="stratification-info-row">
              Prahová hodnota: {state.data.polypharmacy.threshold ?? "-"}
            </div>
            <div className="stratification-info-row">
              Překročen limit:{" "}
              {formatBoolean(state.data.polypharmacy.is_exceeded)}
            </div>
          </div>
        ) : (
          "Žádné informace"
        ),
      },
      {
        label: "Klíčová slova",
        value: renderList(
          state.data.keywords,
          (item) =>
            `${item.reference_name || "-"}${item.source ? ` | ${item.source}` : ""}`,
        ),
      },
    ];
  }, [state.data]);

  if (state.loading) {
    return <h1>Loading...</h1>;
  }

  if (state.error) {
    return <h1>Chyba načítání stratifikace</h1>;
  }

  return (
    <div className="patient-info-table stratification-info-table">
      <div className="header patient-info-cell">
        <strong>Příjmení a jméno pacienta</strong>
      </div>
      <div className="header patient-info-cell">
        <strong>{patientName || "Žádné informace"}</strong>
      </div>

      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <div className="patient-info-cell strong">{row.label}</div>
          <div className="patient-info-cell">{row.value}</div>
        </React.Fragment>
      ))}

      <button
        className="button patient-info-table-button"
        type="button"
        onClick={onBack}
      >
        Zpět
      </button>
    </div>
  );
};
